import apiClient from './apiClient';

const API_URL = '/notifications';

export interface Notification {
  _id: string;
  userID: string;
  type: string;
  title: string;
  message: string;
  link?: string;
  relatedID?: string;
  relatedModel?: string;
  isRead: boolean;
  createdAt: string;
}

export interface NotificationResponse {
  count: number;
  pagination: {
    total: number;
    page: number;
    pages: number;
  };
  unreadCount: number;
  data: Notification[];
}

/**
 * Get user's notifications with pagination
 */
export const getNotifications = async (
  page = 1,
  limit = 10,
  unreadOnly = false
): Promise<NotificationResponse> => {
  try {
    const response = await apiClient.get(
      `${API_URL}?page=${page}&limit=${limit}&unread=${unreadOnly}`
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching notifications:', error);
    throw error;
  }
};

/**
 * Get unread notification count
 */
export const getNotificationCount = async (): Promise<number> => {
  try {
    const response = await apiClient.get(`${API_URL}/count`);

    // Validate response structure
    if (!response?.data?.success) {
      throw new Error('Invalid response from server');
    }

    if (!response.data.data || typeof response.data.data.unreadCount !== 'number') {
      throw new Error('Invalid unreadCount in response');
    }

    return response.data.data.unreadCount;
  } catch (error) {
    console.error('Error fetching notification count:', error);
    // Return 0 as fallback instead of throwing to prevent UI breaking
    return 0;
  }
};

/**
 * Mark a notification as read
 */
export const markAsRead = async (notificationId: string): Promise<Notification> => {
  try {
    const response = await apiClient.put(`${API_URL}/${notificationId}/read`);
    return response.data.data;
  } catch (error) {
    console.error('Error marking notification as read:', error);
    throw error;
  }
};

/**
 * Mark all notifications as read
 */
export const markAllAsRead = async (): Promise<number> => {
  try {
    const response = await apiClient.put(`${API_URL}/read-all`);
    return response.data.data.modified;
  } catch (error) {
    console.error('Error marking all notifications as read:', error);
    throw error;
  }
};

/**
 * Delete a notification
 */
export const deleteNotification = async (notificationId: string): Promise<void> => {
  try {
    await apiClient.delete(`${API_URL}/${notificationId}`);
  } catch (error) {
    console.error('Error deleting notification:', error);
    throw error;
  }
};
